import React, { useState, useEffect } from "react"
import MetaTags from "react-meta-tags"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
} from "reactstrap"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import classnames from "classnames"
import Datatables from "components/Datatables/datatable"
import Select from "react-select"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_red.css"
import { get, post } from "helpers/api_helper"
import {
  ATTENDANCE_URL,
  ATTENDANCE_LOGS_URL,
} from "helpers/url_helper"
import Timezone from "utils/timezone"

const Attendance = () => {
  const [activeTab, setActiveTab] = useState("1")
  const [isLoading, setIsLoading] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [editForm, setEditForm] = useState({
    employee_id: "",
    employee_name: "",
    date: "",
    check_in: "",
    check_out: "",
    status: "Absent",
  })

  const formatTimeForInput = (timeStr) => {
    if (!timeStr || timeStr === "00:00:00" || timeStr === "---" || timeStr === "-" || String(timeStr).toUpperCase() === "NULL") {
      return "";
    }
    const parts = timeStr.split(":");
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return timeStr;
  }

  const handleEditClick = async (row) => {
    setSelectedRow(row)

    const isCheckInMissing = !row.check_in || row.check_in === "00:00:00" || row.check_in === "---" || row.check_in === "-" || String(row.check_in).toUpperCase() === "NULL";
    const isCheckOutMissing = !row.check_out || row.check_out === "00:00:00" || row.check_out === "---" || row.check_out === "-" || String(row.check_out).toUpperCase() === "NULL";

    let initialStatus = row.status || "Absent";

    if (isCheckInMissing && isCheckOutMissing && initialStatus !== "On Leave") {
      try {
        const res = await get(`api/hrms/attendance/check_leave?employee_id=${row.employee_id}&date=${row.date || today}`);
        if (res && res.has_leave) {
          initialStatus = "On Leave";
        }
      } catch (err) {
        console.error("Error checking leave status:", err);
      }
    }

    setEditForm({
      employee_id: row.employee_id,
      employee_name: row.employee_name || `${row.firstname || ""} ${row.lastname || ""}`,
      date: row.date || today,
      check_in: formatTimeForInput(row.check_in),
      check_out: formatTimeForInput(row.check_out),
      status: initialStatus,
    })
    setIsEditModalOpen(true)
  }

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const isBothEmpty = !editForm.check_in && !editForm.check_out;
      const res = await post("api/hrms/attendance/manual_update", {
        employee_id: editForm.employee_id,
        date: editForm.date,
        check_in: editForm.check_in ? `${editForm.check_in}:00` : null,
        check_out: editForm.check_out ? `${editForm.check_out}:00` : null,
        status: isBothEmpty ? editForm.status : "Present"
      });

      if (res && (res.status === 200 || res.status === true)) {
        toastr.success(res.message || "Attendance updated successfully");
        setIsEditModalOpen(false);
        getAttendanceData();
        if (activeTab === "3") {
          getReportData();
        }
      } else {
        toastr.error(res.message || "Failed to update attendance");
      }
    } catch (error) {
      console.error("Failed to update attendance:", error);
      toastr.error("An error occurred while updating attendance");
    }
  }
  const [attendanceData, setAttendanceData] = useState({
    rows: [],
    total_rows: 0,
  })
  const [logsData, setLogsData] = useState({ rows: [], total_rows: 0 })
  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    search: "",
  })
  const [logsPagination, setLogsPagination] = useState({
    limit: 10,
    page: 1,
    search: "",
  })

  const [branchOptions, setBranchOptions] = useState([])
  const [employeeOptions, setEmployeeOptions] = useState([])
  const today = new Date().toISOString().split("T")[0]

  const [filters, setFilters] = useState({
    branch_id: "",
    employee_id: "",
    date: today,
  })

  // Reports tab states
  const [reportFilters, setReportFilters] = useState({
    branch_id: "all",
    employee_id: "all",
    department: "all",
    role: "all",
    dates: [today, today]
  })
  const [reportDates, setReportDates] = useState([new Date(), new Date()])
  const [reportData, setReportData] = useState([])
  const [isReportLoading, setIsReportLoading] = useState(false)
  const [reportEmployeeOptions, setReportEmployeeOptions] = useState([])
  const [deptOptions, setDeptOptions] = useState([])
  const [roleOptions, setRoleOptions] = useState([])

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const brownBtnStyle = {
    backgroundColor: "#3d1e08",
    borderColor: "#3d1e08",
    color: "#fff",
  }
  const greenBtnStyle = {
    backgroundColor: "#159858",
    borderColor: "#159858",
    color: "#fff",
  }

  const getAttendanceData = async () => {
    setIsLoading(true)
    try {
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        search: pagination.search,
        ...(filters.branch_id && { branch_id: filters.branch_id }),
        ...(filters.employee_id && { employee_id: filters.employee_id }),
        ...(filters.date && { date: filters.date }),
      }).toString()
      const res = await get(`${ATTENDANCE_URL}?${queryParams}`)
      if (res && (res.status === 200 || res.status === true)) {
        setAttendanceData(res.response || { rows: [], total_rows: 0 })
      }
    } catch (error) {
      console.error("Attendance fetch error:", error)
    }
    setIsLoading(false)
  }

  const getLogsData = async () => {
    setIsLoading(true)
    try {
      const res = await get(
        `${ATTENDANCE_LOGS_URL}?page=${logsPagination.page}&limit=${logsPagination.limit}&search=${logsPagination.search}`
      )
      if (res && (res.status === 200 || res.status === true)) {
        setLogsData(res.response || { rows: [], total_rows: 0 })
      }
    } catch (error) {
      console.error("Logs fetch error:", error)
    }
    setIsLoading(false)
  }

  const fetchSelectors = async () => {
    const branchRes = await get("api/selectors/getBranchs")
    if (branchRes.status === 200) {
      setBranchOptions(
        branchRes.response.map(b => ({ value: b.id, label: b.title }))
      )
    }
    const empRes = await post("api/selectors/getEmployees", { branch: "all" })
    if (empRes.status === 200) {
      setEmployeeOptions([
        { value: "all", label: "All Employees" },
        ...empRes.response,
      ])
      setReportEmployeeOptions([
        { value: "all", label: "All Employees" },
        ...empRes.response,
      ])
    }

    const deptRes = await get("api/masters?access_type=m01")
    if (deptRes.status === 200 || deptRes.status === true) {
      const rows = deptRes.response?.rows || deptRes.response || []
      setDeptOptions([
        { value: "all", label: "All Departments" },
        ...rows.map(d => ({ value: d.code, label: d.title }))
      ])
    }

    const roleRes = await get("api/masters?access_type=m13")
    if (roleRes.status === 200 || roleRes.status === true) {
      const rows = roleRes.response?.rows || roleRes.response || []
      setRoleOptions([
        { value: "all", label: "All Roles" },
        ...rows.map(r => ({ value: r.code, label: r.title }))
      ])
    }
  }

  const fetchReportEmployees = async (branchVal) => {
    try {
      const empRes = await post("api/selectors/getEmployees", { branch: branchVal })
      if (empRes.status === 200) {
        setReportEmployeeOptions([
          { value: "all", label: "All Employees" },
          ...empRes.response,
        ])
      }
    } catch (error) {
      console.error("Report employees fetch error:", error)
    }
  }

  const getReportData = async (filters = reportFilters) => {
    setIsReportLoading(true)
    try {
      const res = await post("api/reports/attendance", filters)
      if (res && (res.status === 200 || res.status === true)) {
        setReportData(res.response?.data || res.response || [])
      }
    } catch (error) {
      console.error("Report fetch error:", error)
    }
    setIsReportLoading(false)
  }

  useEffect(() => {
    fetchSelectors()
  }, [])

  useEffect(() => {
    if (activeTab === "1") getAttendanceData()
  }, [pagination, activeTab, filters])

  useEffect(() => {
    if (activeTab === "2") getLogsData()
  }, [logsPagination, activeTab])

  useEffect(() => {
    if (activeTab === "3") getReportData()
  }, [activeTab])

  const attendanceColumns = [
    { dataField: "id", text: "#" },
    { dataField: "employee_name", text: "EMPLOYEE" },
    { dataField: "employee_code", text: "EMPLOYEE CODE" },
    { dataField: "date", text: "DATE" },
    {
      dataField: "check_in",
      text: "CHECK IN",
      formatter: (cell) => {
        if (!cell || cell === "00:00:00" || cell === "---" || cell === "-" || String(cell).toUpperCase() === "NULL") {
          return <span style={{ backgroundColor: "#ffebee", color: "#c62828", padding: "4px 8px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px" }}>Punch Missing</span>;
        }
        return cell;
      }
    },
    {
      dataField: "check_out",
      text: "CHECK OUT",
      formatter: (cell) => {
        if (!cell || cell === "00:00:00" || cell === "---" || cell === "-" || String(cell).toUpperCase() === "NULL") {
          return <span style={{ backgroundColor: "#ffebee", color: "#c62828", padding: "4px 8px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px" }}>Punch Missing</span>;
        }
        return cell;
      }
    },
    { dataField: "duration", text: "DURATION" },
    {
      dataField: "status",
      text: "STATUS",
      formatter: cell => {
        let badgeClass = "bg-danger";
        if (cell === "Present") badgeClass = "bg-success";
        else if (cell === "On Leave") badgeClass = "bg-warning text-dark";
        return (
          <span className={`badge ${badgeClass}`}>
            {cell}
          </span>
        );
      },
    },
    {
      dataField: "actions",
      text: "ACTIONS",
      formatter: (cell, row) => {
        const isCheckInMissing = !row.check_in || row.check_in === "00:00:00" || row.check_in === "---" || row.check_in === "-" || String(row.check_in).toUpperCase() === "NULL";
        const isCheckOutMissing = !row.check_out || row.check_out === "00:00:00" || row.check_out === "---" || row.check_out === "-" || String(row.check_out).toUpperCase() === "NULL";
        const isMissing = isCheckInMissing || isCheckOutMissing;
        return (
          <Button
            color={isMissing ? "danger" : "primary"}
            size="sm"
            style={{ fontSize: "11px", padding: "2px 8px" }}
            onClick={() => handleEditClick(row)}
          >
            {isMissing ? "Fix Punch" : "Edit"}
          </Button>
        );
      }
    }
  ]

  const logsColumns = [
    { dataField: "id", text: "#" },
    { dataField: "employee_name", text: "EMPLOYEE" },
    { dataField: "employee_code", text: "EMPLOYEE CODE" },
    { dataField: "punch_time", text: "PUNCH TIME" },
    {
      dataField: "type",
      text: "TYPE",
      formatter: cell => (
        <span className={`badge ${cell === "In" ? "bg-info" : "bg-warning"}`}>
          {cell}
        </span>
      ),
    },
    { dataField: "source", text: "SOURCE" },
  ]

  const reportColumns = [
    { dataField: "id", text: "#", sort: true, formatter: (cell, row, index) => index + 1 },
    { dataField: "employee_name", text: "EMPLOYEE", sort: true },
    { dataField: "employee_code", text: "EMPLOYEE CODE", sort: true },
    { dataField: "branch_name", text: "BRANCH", sort: true },
    { dataField: "department_name", text: "DEPARTMENT", sort: true },
    // { dataField: "role_name", text: "ROLE", sort: true },
    { dataField: "date", text: "DATE", sort: true },
    {
      dataField: "check_in",
      text: "CHECK IN",
      sort: true,
      formatter: (cell) => {
        if (!cell || cell === "00:00:00" || cell === "---" || cell === "-" || String(cell).toUpperCase() === "NULL") {
          return <span style={{ backgroundColor: "#ffebee", color: "#c62828", padding: "4px 8px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px" }}>Punch Missing</span>;
        }
        return cell;
      }
    },
    {
      dataField: "check_out",
      text: "CHECK OUT",
      sort: true,
      formatter: (cell) => {
        if (!cell || cell === "00:00:00" || cell === "---" || cell === "-" || String(cell).toUpperCase() === "NULL") {
          return <span style={{ backgroundColor: "#ffebee", color: "#c62828", padding: "4px 8px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px" }}>Punch Missing</span>;
        }
        return cell;
      }
    },
    { dataField: "duration", text: "DURATION", sort: true },
    {
      dataField: "status",
      text: "STATUS",
      sort: true,
      formatter: cell => {
        let badgeClass = "bg-danger";
        if (cell === "Present") badgeClass = "bg-success";
        else if (cell === "On Leave") badgeClass = "bg-warning text-dark";
        return (
          <span className={`badge ${badgeClass}`}>
            {cell}
          </span>
        );
      },
    },
    {
      dataField: "actions",
      text: "ACTIONS",
      formatter: (cell, row) => {
        const isCheckInMissing = !row.check_in || row.check_in === "00:00:00" || row.check_in === "---" || row.check_in === "-" || String(row.check_in).toUpperCase() === "NULL";
        const isCheckOutMissing = !row.check_out || row.check_out === "00:00:00" || row.check_out === "---" || row.check_out === "-" || String(row.check_out).toUpperCase() === "NULL";
        const isMissing = isCheckInMissing || isCheckOutMissing;
        return (
          <Button
            color={isMissing ? "danger" : "primary"}
            size="sm"
            style={{ fontSize: "11px", padding: "2px 8px" }}
            onClick={() => handleEditClick(row)}
          >
            {isMissing ? "Fix Punch" : "Edit"}
          </Button>
        );
      }
    }
  ]

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Attendance | HRMS</title>
        </MetaTags>
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                <Nav tabs className="nav-tabs-custom border-bottom-0">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "1" })}
                      onClick={() => toggleTab("1")}
                      style={{ cursor: "pointer", border: "none" }}
                    >
                      Attendance
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "2" })}
                      onClick={() => toggleTab("2")}
                      style={{ cursor: "pointer", border: "none" }}
                    >
                      Logs
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "3" })}
                      onClick={() => toggleTab("3")}
                      style={{ cursor: "pointer", border: "none" }}
                    >
                      Reports
                    </NavLink>
                  </NavItem>
                </Nav>
              </div>

              <TabContent activeTab={activeTab} className="p-0 text-muted">
                <TabPane tabId="1" className="pt-2">
                  <div className="cardfilters">
                    <div
                      className="text-uppercase mb-3"
                      style={{
                        fontWeight: "600",
                        fontSize: "12px",
                        color: "#555",
                      }}
                    >
                      Attendance Filter
                    </div>
                    <Row className="mb-4 g-2">
                      <Col md={3}>
                        <Select
                          placeholder="Select Branch"
                          options={branchOptions}
                          value={
                            branchOptions.find(
                              b => b.value === filters.branch_id
                            ) || null
                          }
                          onChange={e =>
                            setFilters(prev => ({
                              ...prev,
                              branch_id: e ? e.value : "",
                            }))
                          }
                          isClearable
                        />
                      </Col>
                      <Col md={3}>
                        <Select
                          placeholder="Select Employee"
                          options={employeeOptions}
                          value={
                            employeeOptions.find(
                              emp => emp.value === filters.employee_id
                            ) || null
                          }
                          onChange={e =>
                            setFilters(prev => ({
                              ...prev,
                              employee_id: e ? e.value : "",
                            }))
                          }
                          isClearable
                        />
                      </Col>
                      <Col md={2}>
                        <Input
                          type="date"
                          className="form-control"
                          value={filters.date}
                          onChange={e =>
                            setFilters(prev => ({
                              ...prev,
                              date: e.target.value,
                            }))
                          }
                        />
                      </Col>
                      <Col md={4} className="d-flex">
                        <Button
                          style={brownBtnStyle}
                          className="flex-grow-1"
                          onClick={() =>
                            setFilters({
                              branch_id: "",
                              employee_id: "",
                              date: today,
                            })
                          }
                        >
                          Reset
                        </Button>
                      </Col>
                    </Row>
                  </div>

                  <Datatables
                    title="Daily Attendance"
                    titleClass="h5"
                    columns={attendanceColumns}
                    rows={attendanceData.rows}
                    keyField="id"
                    rowsLength={attendanceData.total_rows}
                    isAdd={false}
                    isSearch={true}
                    loading={isLoading}
                    ssr={setPagination}
                  />
                </TabPane>

                <TabPane tabId="2" className="pt-2">
                  <Datatables
                    title="Punch Logs"
                    titleClass="h5"
                    columns={logsColumns}
                    rows={logsData.rows}
                    keyField="id"
                    rowsLength={logsData.total_rows}
                    isAdd={false}
                    isSearch={true}
                    loading={isLoading}
                    ssr={setLogsPagination}
                  />
                </TabPane>

                <TabPane tabId="3" className="pt-2">
                  <div className="cardfilters">
                    <div className="text-uppercase mb-3" style={{ fontWeight: "600", fontSize: "12px", color: "#555" }}>
                      Reports Filter
                    </div>
                    <Row className="mb-4 g-2">
                      <Col md={3}>
                        <Select
                          placeholder="Select Branch"
                          options={branchOptions}
                          value={branchOptions.find(b => b.value === reportFilters.branch_id) || null}
                          onChange={e => {
                            const val = e ? e.value : "all"
                            setReportFilters(prev => ({ ...prev, branch_id: val, employee_id: "all" }))
                            fetchReportEmployees(val)
                          }}
                          isClearable
                        />
                      </Col>
                      <Col md={3}>
                        <Select
                          placeholder="Select Employee"
                          options={reportEmployeeOptions}
                          value={reportEmployeeOptions.find(emp => emp.value === reportFilters.employee_id) || null}
                          onChange={e => setReportFilters(prev => ({ ...prev, employee_id: e ? e.value : "all" }))}
                          isClearable
                        />
                      </Col>
                      <Col md={2}>
                        <Select
                          placeholder="Select Department"
                          options={deptOptions}
                          value={deptOptions.find(d => d.value === reportFilters.department) || null}
                          onChange={e => setReportFilters(prev => ({ ...prev, department: e ? e.value : "all" }))}
                          isClearable
                        />
                      </Col>
                      <Col md={2}>
                        <Select
                          placeholder="Select Role"
                          options={roleOptions}
                          value={roleOptions.find(r => r.value === reportFilters.role) || null}
                          onChange={e => setReportFilters(prev => ({ ...prev, role: e ? e.value : "all" }))}
                          isClearable
                        />
                      </Col>
                      <Col md={3}>
                        <Flatpickr
                          className="form-control d-block"
                          placeholder="DD-MM-YYYY (Date Range)"
                          options={{
                            mode: "range",
                            dateFormat: "d-m-Y"
                          }}
                          value={reportDates}
                          onChange={(dates) => {
                            const formattedDates = dates.map(d => Timezone(d));
                            setReportDates(dates);
                            setReportFilters(prev => ({ ...prev, dates: formattedDates }));
                          }}
                        />
                      </Col>
                      <Col md={4} className="d-flex align-items-center">
                        <Button
                          style={greenBtnStyle}
                          className="me-2 px-4"
                          onClick={() => getReportData()}
                          disabled={isReportLoading}
                        >
                          Filter
                        </Button>
                        <Button
                          style={brownBtnStyle}
                          className="px-4"
                          onClick={() => {
                            const resetObj = {
                              branch_id: "all",
                              employee_id: "all",
                              department: "all",
                              role: "all",
                              dates: [today, today]
                            }
                            setReportFilters(resetObj)
                            setReportDates([new Date(), new Date()])
                            fetchReportEmployees("all")
                            getReportData(resetObj)
                          }}
                          disabled={isReportLoading}
                        >
                          Reset
                        </Button>
                      </Col>
                    </Row>
                  </div>

                  {/* Summary Metrics */}
                  <Row className="mb-4">
                    <Col md={4} sm={6}>
                      <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: "4px solid #159858", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                        <CardBody className="p-3">
                          <div className="text-muted text-uppercase" style={{ fontSize: "11px", fontWeight: "600" }}>Total Present</div>
                          <h4 className="mb-0 text-success font-weight-bold" style={{ fontWeight: "700" }}>
                            {reportData.filter(r => r.status === "Present").length}
                          </h4>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md={4} sm={6}>
                      <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: "4px solid #ff3366", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                        <CardBody className="p-3">
                          <div className="text-muted text-uppercase" style={{ fontSize: "11px", fontWeight: "600" }}>Total Absent</div>
                          <h4 className="mb-0 text-danger font-weight-bold" style={{ fontWeight: "700" }}>
                            {reportData.filter(r => r.status === "Absent" || r.status === "Leave").length}
                          </h4>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md={4} sm={12}>
                      <Card className="h-100 border-0 shadow-sm" style={{ borderLeft: "4px solid #777", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                        <CardBody className="p-3">
                          <div className="text-muted text-uppercase" style={{ fontSize: "11px", fontWeight: "600" }}>Total Records</div>
                          <h4 className="mb-0 text-dark font-weight-bold" style={{ fontWeight: "700" }}>
                            {reportData.length}
                          </h4>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  <Datatables
                    title="Attendance Summary Report"
                    titleClass="h5"
                    columns={reportColumns}
                    rows={reportData}
                    keyField="id"
                    rowsLength={reportData.length}
                    isAdd={false}
                    isSearch={true}
                    excel={true}
                    csv={true}
                    loading={isReportLoading}
                    is_remote={false}
                  />
                </TabPane>
              </TabContent>

              {/* Manual Attendance Update Modal */}
              <Modal isOpen={isEditModalOpen} toggle={() => setIsEditModalOpen(false)} centered>
                <ModalHeader toggle={() => setIsEditModalOpen(false)} className="border-bottom-0 pb-0">
                  <span className="font-size-16 font-weight-bold text-dark">
                    Manual Attendance Entry
                  </span>
                </ModalHeader>
                <form onSubmit={handleEditSubmit}>
                  <ModalBody className="py-3">
                    <div className="mb-3">
                      <Label className="form-label text-muted font-size-12 mb-1">Employee</Label>
                      <Input
                        type="text"
                        className="form-control bg-light"
                        value={`${editForm.employee_name} (Code: ${editForm.employee_id})`}
                        disabled
                      />
                    </div>
                    <div className="mb-3">
                      <Label className="form-label font-size-12 mb-1">Date</Label>
                      <Input
                        type="date"
                        className="form-control"
                        value={editForm.date}
                        onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                        required
                      />
                    </div>
                    <Row className="g-2">
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label font-size-12 mb-1">Check In Time</Label>
                          <Input
                            type="time"
                            className="form-control"
                            value={editForm.check_in}
                            onChange={(e) => setEditForm({ ...editForm, check_in: e.target.value })}
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label className="form-label font-size-12 mb-1">Check Out Time</Label>
                          <Input
                            type="time"
                            className="form-control"
                            value={editForm.check_out}
                            onChange={(e) => setEditForm({ ...editForm, check_out: e.target.value })}
                          />
                        </div>
                      </Col>
                    </Row>
                    {!editForm.check_in && !editForm.check_out && (
                      <div className="mb-3">
                        <Label className="form-label font-size-12 mb-1">Status</Label>
                        <Input
                          type="select"
                          className="form-select"
                          value={editForm.status || "Absent"}
                          onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        >
                          <option value="Absent">Absent</option>
                          <option value="On Leave">On Leave</option>
                        </Input>
                      </div>
                    )}
                  </ModalBody>
                  <ModalFooter className="border-top-0 pt-0">
                    <Button
                      type="button"
                      color="secondary"
                      className="px-4"
                      onClick={() => setIsEditModalOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      style={greenBtnStyle}
                      className="px-4"
                    >
                      Save Changes
                    </Button>
                  </ModalFooter>
                </form>
              </Modal>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Attendance
